import { Clock } from "lucide-react";

import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn, firstName } from "@/lib/utils";

export type CandidateTimeUpCardProps = {
  /** Candidate's full name; only the first name is shown in the greeting. */
  candidateName?: string | null;
  /** Test name, woven into the default body when present. */
  testName?: string | null;
  /** Per-company time-limit message; replaces the default body when set. */
  timeLimitMessage?: string | null;
  className?: string;
};

/**
 * The terminal card a candidate sees when the clock runs out instead of them
 * submitting (BUG-6). Sibling of `CandidateDoneCard`, and shared the same way:
 * the real candidate route (`routes/Done.tsx`) and the recruiter's live preview
 * in Settings → Completion both render THIS component, so the preview can never
 * drift from the real screen.
 *
 * Tone is deliberately NEUTRAL, not failure: running out of time is an expected
 * end to an attempt. Same layout and `bg-secondary` icon well as its two
 * siblings, but a `Clock` in `text-info` — distinct from the success card's
 * green check and, crucially, from the error card's red X, which is what this
 * screen used to be mistaken for. Colour is never the only signal: the icon and
 * the copy both say "time", so the card still reads correctly monochrome.
 */
export function CandidateTimeUpCard({
  candidateName,
  testName,
  timeLimitMessage,
  className,
}: CandidateTimeUpCardProps) {
  const hasMessage = !!timeLimitMessage && timeLimitMessage.trim() !== "";
  return (
    <Card className={cn("w-full max-w-lg text-center", className)}>
      <CardHeader>
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-secondary">
          <Clock className="h-6 w-6 text-info" aria-hidden />
        </div>
        <CardTitle>
          Time&apos;s up{candidateName ? `, ${firstName(candidateName)}` : ""}.
        </CardTitle>
        <CardDescription
          className={cn(
            // break-words (overflow-wrap: break-word) only breaks a token that
            // cannot fit at all, so prose is unaffected — but the recruiter
            // copy is free text up to 1000 chars and an unbroken run (a long
            // URL, a pasted token) would otherwise spill past the card and
            // scroll the candidate's page sideways.
            "space-y-1 text-smd break-words",
            hasMessage && "whitespace-pre-line",
          )}
        >
          {hasMessage ? (
            timeLimitMessage
          ) : (
            <>
              {/* Deliberately says "saved", never "graded": a timed-out attempt
                  is marked `expired` — the answers are persisted, but it is not
                  submitted or graded. Promising a score here would be a claim
                  the backend doesn't make.

                  "as you went" is load-bearing, not filler. Saves are enqueued
                  per navigation, so everything the candidate moved past IS
                  stored — but the one answer still on screen at the buzzer can
                  be lost: Question.tsx's handleTimeUp abandons a save that
                  races the deadline, and the answer queue treats the resulting
                  410 as terminal (no retry). A flat "your responses have been
                  saved" would assert that final write actually landed. */}
              {testName ? (
                <>
                  Your time for{" "}
                  <span className="font-medium text-foreground">{testName}</span>{" "}
                  is up
                </>
              ) : (
                "Your time is up"
              )}
              . Your answers were saved as you went. The recruiter will review
              them and reach out by email if appropriate.
            </>
          )}
        </CardDescription>
      </CardHeader>
    </Card>
  );
}
